import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PushPin as PinIcon,
  PushPinOutlined as UnpinIcon,
} from "@mui/icons-material";
import { categoriesAPI } from "../../services/api";
import { useSnackbar } from "../../contexts/SnackbarContext";
import CustomContainer from "../../components/common/CustomContainer";

const getLocalizedContent = (content, language) => {
  if (!content) return "";
  const values = content.split("|");
  switch (language) {
    case "fr":
      return values[1] || values[0] || ""; // Fallback to English if French is empty
    case "ar":
      return values[2] || values[0] || ""; // Fallback to English if Arabic is empty
    default:
      return values[0] || ""; // English is default
  }
};

const Categories = () => {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: {
      en: "",
      fr: "",
      ar: "",
    },
    description: {
      en: "",
      fr: "",
      ar: "",
    },
  });
  const { showSnackbar } = useSnackbar();

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      const data = response.data?.categories || response.data || [];
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      showSnackbar(t("categories.messages.loadError"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleDialogOpen = (category = null) => {
    if (category) {
      setSelectedCategory(category);
      // Split the multilingual values using the separator
      const names = (category.name || "").split("|");
      const descriptions = (category.description || "").split("|");
      setFormData({
        name: {
          en: names[0] || "",
          fr: names[1] || "",
          ar: names[2] || "",
        },
        description: {
          en: descriptions[0] || "",
          fr: descriptions[1] || "",
          ar: descriptions[2] || "",
        },
      });
    } else {
      setSelectedCategory(null);
      setFormData({
        name: {
          en: "",
          fr: "",
          ar: "",
        },
        description: {
          en: "",
          fr: "",
          ar: "",
        },
      });
    }
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedCategory(null);
  };

  const validateForm = () => {
    // At least the English name is required
    if (!formData.name.en.trim()) {
      showSnackbar(t("categories.form.nameEnglishRequired"), "error");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      // Combine multilingual values with the separator
      const submissionData = {
        name: [formData.name.en, formData.name.fr, formData.name.ar].join("|"),
        description: [
          formData.description.en,
          formData.description.fr,
          formData.description.ar,
        ].join("|"),
      };

      if (selectedCategory) {
        await categoriesAPI.update(selectedCategory.id, submissionData);
        showSnackbar(t("categories.messages.updateSuccess"), "success");
      } else {
        await categoriesAPI.create(submissionData);
        showSnackbar(t("categories.messages.createSuccess"), "success");
      }
      handleDialogClose();
      fetchCategories();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Operation failed";
      showSnackbar(errorMessage, "error");
      console.error("Category operation error:", error);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm(t("categories.messages.confirmDelete"))) {
      return;
    }

    try {
      await categoriesAPI.delete(categoryId);
      showSnackbar(t("categories.messages.deleteSuccess"), "success");
      fetchCategories();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || t("categories.messages.deleteFailed");
      showSnackbar(errorMessage, "error");
      console.error("Delete category error:", error);
    }
  };

  const handleTogglePin = async (category) => {
    try {
      await categoriesAPI.togglePin(category.id);
      showSnackbar(
        category.pinned
          ? t("categories.messages.unpinnedSuccess")
          : t("categories.messages.pinnedSuccess"),
        "success"
      );
      fetchCategories();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        t("categories.messages.operationFailed");
      showSnackbar(errorMessage, "error");
      console.error("Pin/unpin error:", error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <CustomContainer>
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4">{t("categories.title")}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleDialogOpen()}
        >
          {t("categories.add")}
        </Button>
      </Box>

      {categories.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("categories.messages.noCategories")}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("categories.fields.name")}</TableCell>
                <TableCell>{t("categories.fields.description")}</TableCell>
                <TableCell align="center">
                  {t("categories.fields.booksCount")}
                </TableCell>
                <TableCell align="center">
                  {t("categories.fields.pinned")}
                </TableCell>
                <TableCell align="center">
                  {t("categories.fields.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>
                    {getLocalizedContent(category.name, i18n.language)}
                  </TableCell>
                  <TableCell>
                    {getLocalizedContent(category.description, i18n.language) ||
                      "-"}
                  </TableCell>
                  <TableCell align="center">
                    {Number(category.bookcount)}
                  </TableCell>
                  <TableCell align="center">
                    {category.pinned ? t("common.yes") : t("common.no")}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={
                        category.pinned
                          ? t("categories.actions.unpin")
                          : t("categories.actions.pin")
                      }
                    >
                      <IconButton
                        onClick={() => handleTogglePin(category)}
                        color={category.pinned ? "primary" : "default"}
                        size="small"
                      >
                        {category.pinned ? <PinIcon /> : <UnpinIcon />}
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      onClick={() => handleDialogOpen(category)}
                      color="primary"
                      size="small"
                      title={t("categories.actions.edit")}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteCategory(category.id)}
                      color="error"
                      size="small"
                      title={t("categories.actions.delete")}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedCategory ? t("categories.edit") : t("categories.new")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            {/* English Fields */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {t("categories.languages.english")}
              </Typography>
              <TextField
                label={t("categories.form.nameEnglish")}
                value={formData.name.en}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    name: { ...formData.name, en: e.target.value },
                  })
                }
                fullWidth
                required
                autoFocus
                error={!formData.name.en.trim()}
                helperText={
                  !formData.name.en.trim() &&
                  t("categories.form.nameEnglishRequired")
                }
              />
              <TextField
                label={t("categories.form.descriptionEnglish")}
                value={formData.description.en}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    description: {
                      ...formData.description,
                      en: e.target.value,
                    },
                  })
                }
                fullWidth
                multiline
                rows={2}
                placeholder={t("categories.form.descriptionEnglishPlaceholder")}
              />
            </Box>

            {/* French Fields */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {t("categories.languages.french")}
              </Typography>
              <TextField
                label={t("categories.form.nameFrench")}
                value={formData.name.fr}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    name: { ...formData.name, fr: e.target.value },
                  })
                }
                fullWidth
              />
              <TextField
                label={t("categories.form.descriptionFrench")}
                value={formData.description.fr}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    description: {
                      ...formData.description,
                      fr: e.target.value,
                    },
                  })
                }
                fullWidth
                multiline
                rows={2}
                placeholder={t("categories.form.descriptionFrenchPlaceholder")}
              />
            </Box>

            {/* Arabic Fields */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {t("categories.languages.arabic")}
              </Typography>
              <TextField
                label={t("categories.form.nameArabic")}
                value={formData.name.ar}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    name: { ...formData.name, ar: e.target.value },
                  })
                }
                fullWidth
                dir="rtl"
              />
              <TextField
                label={t("categories.form.descriptionArabic")}
                value={formData.description.ar}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    description: {
                      ...formData.description,
                      ar: e.target.value,
                    },
                  })
                }
                fullWidth
                multiline
                rows={2}
                dir="rtl"
                placeholder={t("categories.form.descriptionArabicPlaceholder")}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>{t("common.cancel")}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name.en.trim()}
          >
            {selectedCategory ? t("common.update") : t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </CustomContainer>
  );
};

export default Categories;
